import { useContext, useState } from 'react';
import PageDataContext from '../context/PageDataContext';

/**
 * A cache for storing data for each page in the SPA React App.
 *
 * **Note**: The `setState` function returned is async under the hood and may cause
 * race conditions or the like if there a `useEffect` that is triggered if the value is changed.
 *
 * @param key The key store this in IndexDb
 * @param initialValue the initial value
 */
export function usePageDataCache<T>(
    key: string,
    initialValue: T,
): [T, (value: React.SetStateAction<T>) => void] {
    const { getItem, setItem } = useContext(PageDataContext);
    const [storedValue, setStoredValue] = useState<T>(() => {
        const setValueFromCache = async () => {
            const value = await getItem(initialValue, key);
            if (!!value) {
                setStoredValue(value);
            }
        };

        setValueFromCache();

        return initialValue;
    });

    const setValue = (value: React.SetStateAction<T>) => {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setItem(key, valueToStore);
        setStoredValue(value);
    };

    return [storedValue, setValue];
}
